/**
 * Google Analytics 4 helpers.
 *
 * The gtag.js script and initial `config` call are injected dynamically by
 * `GA4Bootstrap` (App.js) once the operator's `ga4_measurement_id` setting
 * loads — see that component for why this can't live statically in
 * index.html anymore. This module handles two things:
 *
 *  1. SPA route changes — React Router navigation doesn't trigger a real
 *     pageload, so GA4 would otherwise count the entire session as a
 *     single page_view. usePageTracking() fires a synthetic page_view
 *     on every location change.
 *
 *  2. E-commerce + custom events — small wrappers around `gtag('event', …)`
 *     for things like "view_item", "add_to_cart", "purchase". These are
 *     the GA4-recommended e-commerce event names (do NOT rename them —
 *     GA4 only auto-builds reports when the event + parameter names match
 *     the spec).
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSiteSettings } from '../context/SiteSettingsContext';

const hasGtag = () => typeof window !== 'undefined' && typeof window.gtag === 'function';

/** Hook: call once from a component inside <Router>. Fires page_view on every route change. */
export function usePageTracking() {
  const location = useLocation();
  const { ga4MeasurementId } = useSiteSettings();
  useEffect(() => {
    if (typeof window.fbq === 'function') window.fbq('track', 'PageView');
    if (!hasGtag()) return;
    window.gtag('event', 'page_view', {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
      ...(ga4MeasurementId ? { send_to: ga4MeasurementId } : {}),
    });
  }, [location, ga4MeasurementId]);
}

/** GA4 shop events and their Meta Pixel names (catalog ads read these). */
const META_EVENTS = { view_item: 'ViewContent', add_to_cart: 'AddToCart', begin_checkout: 'InitiateCheckout', purchase: 'Purchase' };

/**
 * Meta wants the catalogue's product IDs (the feeds use the product ID) and
 * de-duplicates the purchase against the server's Conversions API event by
 * the order number.
 */
export function toMetaEvent(name, params = {}) {
  const event = META_EVENTS[name];
  if (!event) return null;
  const items = params.items || [];
  const data = {
    content_type: 'product',
    content_ids: items.map((i) => String(i.product_id ?? i.item_id)),
    contents: items.map((i) => ({ id: String(i.product_id ?? i.item_id), quantity: i.quantity || 1, item_price: i.price })),
    num_items: items.reduce((n, i) => n + (i.quantity || 1), 0),
    value: params.value,
    currency: params.currency,
  };
  const options = name === 'purchase' && params.transaction_id ? { eventID: `order-${params.transaction_id}` } : undefined;
  return { event, data, options };
}

/** Fire a custom GA4 event (and its Meta Pixel twin). Safe to call when neither is loaded. */
export function trackEvent(name, params = {}) {
  if (hasGtag()) window.gtag('event', name, params);
  if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
    const meta = toMetaEvent(name, params);
    if (meta) window.fbq('track', meta.event, meta.data, meta.options);
  }
}
